import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Button,
  Form,
  Spinner,
  Alert,
  Row,
  Col,
  Card,
  Badge,
} from 'react-bootstrap';
import {
  FaArrowLeft,
  FaListOl,
  FaInfoCircle,
  FaSave,
  FaGripVertical,
  FaArrowRight,
  FaTrash,
  FaSearch,
  FaTimes,
} from 'react-icons/fa';
import ObjectConfigApi from '../../api/ObjectConfigApi';
import {
  parseLineItemWizardConfig,
  LINE_ITEM_WIZARD_STRING_KEYS,
  getLineItemWizardStringKeyLabel,
  DEFAULT_SEARCH_LABELS,
  DEFAULT_EDIT_LABELS,
  humanizeLineItemKey,
  deriveProductColumnsForWizard,
  getLineItemWizardColumnApiName,
} from '../layout/lineItemWizardConfig';
import '../layout/css/EnhancedListViewDialog.css';

function resolveSearchColumnLabel(form, key) {
  return form.searchColumns[key]?.label || DEFAULT_SEARCH_LABELS[key] || humanizeLineItemKey(key);
}

function resolveEditColumnLabel(form, key) {
  return form.editColumns[key]?.label || DEFAULT_EDIT_LABELS[key] || humanizeLineItemKey(key);
}

/** DB / REST field name (`name`, `product_code`, …) vs wizard column key (`productName`). */
function WizardColumnApiHint({ columnKey }) {
  const api = getLineItemWizardColumnApiName(columnKey);
  const isUiOnly = api === null;
  return (
    <>
      <code className="text-muted d-block" style={{ fontSize: '11px' }} title="API / database column name">
        {isUiOnly ? '—' : api}
      </code>
      {(isUiOnly || (typeof api === 'string' && api !== columnKey)) && (
        <div className="text-muted" style={{ fontSize: '10px' }}>
          {isUiOnly ? (
            <>
              UI-only · <code style={{ fontSize: '10px' }}>{columnKey}</code>
            </>
          ) : (
            <>
              Wizard key <code style={{ fontSize: '10px' }}>{columnKey}</code>
            </>
          )}
        </div>
      )}
    </>
  );
}

function parseRootConfig(config) {
  if (!config) {
    return {};
  }
  if (typeof config === 'string') {
    try {
      return JSON.parse(config) || {};
    } catch {
      return {};
    }
  }
  return typeof config === 'object' ? config : {};
}

function columnLabelMapForSave(allKeys, columns, defaults) {
  const out = {};
  for (const k of allKeys) {
    const lbl = columns?.[k]?.label ?? defaults[k] ?? humanizeLineItemKey(k);
    out[k] = { label: String(lbl ?? humanizeLineItemKey(k)) };
  }
  return out;
}

function buildSavePayload(form) {
  const strings = {};
  for (const k of LINE_ITEM_WIZARD_STRING_KEYS) {
    const v = form.strings?.[k];
    if (v && String(v).trim()) {
      strings[k] = String(v).trim();
    }
  }
  const searchUnion = form.searchUnionKeys?.length ? form.searchUnionKeys : Object.keys(form.searchColumns || {});
  const editUnion = form.editUnionKeys?.length ? form.editUnionKeys : Object.keys(form.editColumns || {});
  const lineItemWizard = {
    searchColumnOrder: [...(form.searchSelectedKeys || [])],
    editColumnOrder: [...(form.editSelectedKeys || [])],
    searchColumns: columnLabelMapForSave(searchUnion, form.searchColumns, DEFAULT_SEARCH_LABELS),
    editColumns: columnLabelMapForSave(editUnion, form.editColumns, DEFAULT_EDIT_LABELS),
  };
  if (Object.keys(strings).length > 0) {
    lineItemWizard.strings = strings;
  }
  return { lineItemWizard };
}

/** Normalize GET /objectconfig/:name JSON into `{ object_name, fields[], ... }` */
function normalizeObjectConfigPayload(res) {
  if (!res || res.success === false) {
    return null;
  }
  if (res.success && res.data) {
    return res.data;
  }
  if (res?.data?.success && res?.data?.data) {
    return res.data.data;
  }
  if (typeof res.object_name === 'string' && Array.isArray(res.fields)) {
    return res;
  }
  return null;
}

async function fetchProductConfigWithFieldsForWizard() {
  const names = ['product', 'Product'];
  for (let i = 0; i < names.length; i += 1) {
    try {
      const res = await ObjectConfigApi.fetchConfigObjectByNameWithFields(names[i]);
      const payload = normalizeObjectConfigPayload(res);
      if (payload && Array.isArray(payload.fields) && payload.fields.length > 0) {
        return payload;
      }
    } catch {
      /* try alternate API name */
    }
  }
  return null;
}

/** Search filter for available-keys palette */
function filterKeysByTerm(keys, columns, defaults, term) {
  const t = String(term || '').trim().toLowerCase();
  if (!t) {
    return keys;
  }
  return keys.filter((k) => {
    const lbl = (columns?.[k]?.label || defaults[k] || humanizeLineItemKey(k) || k).toLowerCase();
    const id = k.toLowerCase();
    return lbl.includes(t) || id.includes(t);
  });
}

const LineItemWizardConfigPanel = ({
  objectName,
  objectLabel,
  onBack,
}) => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchAvailTerm, setSearchAvailTerm] = useState('');
  const [editAvailTerm, setEditAvailTerm] = useState('');
  const [productPaletteWarning, setProductPaletteWarning] = useState('');
  const [draggedSearch, setDraggedSearch] = useState(null);
  const [dragOverSearch, setDragOverSearch] = useState(null);
  const [draggedEdit, setDraggedEdit] = useState(null);
  const [dragOverEdit, setDragOverEdit] = useState(null);

  const searchAvailableKeys = useMemo(() => {
    if (!form) {
      return [];
    }
    const pool = form.searchUnionKeys?.length ? form.searchUnionKeys : Object.keys(form.searchColumns || {});
    return pool.filter((k) => !form.searchSelectedKeys.includes(k));
  }, [form]);

  const editAvailableKeys = useMemo(() => {
    if (!form) {
      return [];
    }
    const pool = form.editUnionKeys?.length ? form.editUnionKeys : Object.keys(form.editColumns || {});
    return pool.filter((k) => !form.editSelectedKeys.includes(k));
  }, [form]);

  const filteredSearchAvailable = useMemo(
    () =>
      filterKeysByTerm(searchAvailableKeys, form?.searchColumns, DEFAULT_SEARCH_LABELS, searchAvailTerm),
    [searchAvailableKeys, form?.searchColumns, searchAvailTerm]
  );

  const filteredEditAvailable = useMemo(
    () =>
      filterKeysByTerm(editAvailableKeys, form?.editColumns, DEFAULT_EDIT_LABELS, editAvailTerm),
    [editAvailableKeys, form?.editColumns, editAvailTerm]
  );

  const load = useCallback(async () => {
    if (!objectName) {
      return;
    }
    setLoading(true);
    setError('');
    setSuccess('');
    setProductPaletteWarning('');
    try {
      const [parentRes, productPd] = await Promise.all([
        ObjectConfigApi.fetchConfigObjectByNameWithFields(objectName),
        fetchProductConfigWithFieldsForWizard(),
      ]);

      let data = null;
      if (parentRes?.success && parentRes.data) {
        data = parentRes.data;
      } else if (parentRes?.data?.success && parentRes.data?.data) {
        data = parentRes.data.data;
      }
      if (!data) {
        setError('Failed to load object configuration.');
        setForm(null);
        return;
      }

      let productFieldNames = [];
      let productLabelByFieldName = {};
      if (productPd) {
        const extracted = deriveProductColumnsForWizard(productPd);
        productFieldNames = extracted.fieldNames;
        productLabelByFieldName = extracted.labelByFieldName;
      }
      if (productFieldNames.length === 0) {
        setProductPaletteWarning(
          'Could not load fields from the Product catalog object. Only built-in wizard columns appear until the Product object is available (check object name, permissions, or that the tenant defines Product fields).'
        );
      }

      const root = parseRootConfig(data.config);
      const parsed = parseLineItemWizardConfig(root, {
        productFieldNames,
        productLabelByFieldName,
        preferBuiltinColumnPickerDefaults: true,
      });
      setForm({
        ...parsed,
        searchSelectedKeys: [...parsed.searchSelectedKeys],
        editSelectedKeys: [...parsed.editSelectedKeys],
      });
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Failed to load configuration');
      setForm(null);
    } finally {
      setLoading(false);
    }
  }, [objectName]);

  useEffect(() => {
    load();
  }, [load]);

  const setColumnLabel = (table, key, label) => {
    setForm((f) => {
      if (!f) {
        return f;
      }
      const blockKey = table === 'search' ? 'searchColumns' : 'editColumns';
      const prev = f[blockKey][key] || {};
      return {
        ...f,
        [blockKey]: {
          ...f[blockKey],
          [key]: { ...prev, label },
        },
      };
    });
  };

  const moveSearchToSelected = key => {
    setForm(f => {
      if (!f || f.searchSelectedKeys.includes(key)) return f;
      return {
        ...f,
        searchSelectedKeys: [...f.searchSelectedKeys, key],
      };
    });
    setError('');
  };

  const moveSearchToAvailable = key => {
    setForm(f => {
      if (!f) return f;
      const next = f.searchSelectedKeys.filter(k => k !== key);
      if (next.length === 0) return f;
      return { ...f, searchSelectedKeys: next };
    });
  };

  const moveEditToSelected = key => {
    setForm(f => {
      if (!f || f.editSelectedKeys.includes(key)) return f;
      return {
        ...f,
        editSelectedKeys: [...f.editSelectedKeys, key],
      };
    });
    setError('');
  };

  const moveEditToAvailable = key => {
    setForm(f => {
      if (!f) return f;
      const next = f.editSelectedKeys.filter(k => k !== key);
      if (next.length === 0) return f;
      return { ...f, editSelectedKeys: next };
    });
  };

  const handleSearchDragStart = (e, index) => {
    setDraggedSearch(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleSearchDragOver = (e, index) => {
    e.preventDefault();
    if (draggedSearch !== null && draggedSearch !== index) {
      setDragOverSearch(index);
    }
  };

  const handleSearchDragLeave = () => {
    setDragOverSearch(null);
  };

  const handleSearchDrop = (e, index) => {
    e.preventDefault();
    if (draggedSearch === null || draggedSearch === index || !form) {
      setDraggedSearch(null);
      setDragOverSearch(null);
      return;
    }
    const next = [...form.searchSelectedKeys];
    const [moved] = next.splice(draggedSearch, 1);
    next.splice(index, 0, moved);
    setForm({ ...form, searchSelectedKeys: next });
    setDraggedSearch(null);
    setDragOverSearch(null);
  };

  const handleSearchDragEnd = () => {
    setDraggedSearch(null);
    setDragOverSearch(null);
  };

  const handleEditDragStart = (e, index) => {
    setDraggedEdit(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleEditDragOver = (e, index) => {
    e.preventDefault();
    if (draggedEdit !== null && draggedEdit !== index) {
      setDragOverEdit(index);
    }
  };

  const handleEditDragLeave = () => {
    setDragOverEdit(null);
  };

  const handleEditDrop = (e, index) => {
    e.preventDefault();
    if (draggedEdit === null || draggedEdit === index || !form) {
      setDraggedEdit(null);
      setDragOverEdit(null);
      return;
    }
    const next = [...form.editSelectedKeys];
    const [moved] = next.splice(draggedEdit, 1);
    next.splice(index, 0, moved);
    setForm({ ...form, editSelectedKeys: next });
    setDraggedEdit(null);
    setDragOverEdit(null);
  };

  const handleEditDragEnd = () => {
    setDraggedEdit(null);
    setDragOverEdit(null);
  };

  const handleSave = async () => {
    if (!form) return;
    if (form.searchSelectedKeys.length === 0 || form.editSelectedKeys.length === 0) {
      setError('Keep at least one column in each Selected list (search and edit tables).');
      return;
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const { lineItemWizard } = buildSavePayload(form);
      const res = await ObjectConfigApi.saveLineItemWizardConfig(objectName, lineItemWizard);
      if (res?.success) {
        setSuccess('Line item configuration saved. Users will see it after reopening a record detail.');
        await load();
      } else {
        setError(res?.message || 'Save failed');
      }
    } catch (e) {
      console.error(e);
      setError(e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const title = `Line items wizard – ${objectLabel || objectName}`;

  if (loading) {
    return (
      <div className="p-4 text-center">
        <Spinner animation="border" className="me-2" />
        Loading line item settings…
      </div>
    );
  }

  if (!form) {
    return (
      <div className="p-3">
        <Button variant="outline-secondary" size="sm" onClick={onBack} className="mb-3">
          <FaArrowLeft className="me-1" />
          Back
        </Button>
        {error && <Alert variant="danger">{error}</Alert>}
      </div>
    );
  }

  return (
    <div className="line-item-wizard-config-panel px-2">
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
        <div>
          <Button variant="outline-secondary" size="sm" onClick={onBack} className="me-2">
            <FaArrowLeft className="me-1" />
            Back to object
          </Button>
          <h2 className="d-inline h5 mb-0 align-middle">
            <FaListOl className="me-2 text-primary" />
            {title}
          </h2>
        </div>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner animation="border" size="sm" className="me-2" /> : <FaSave className="me-2" />}
          Save
        </Button>
      </div>

      <p className="text-muted small mb-3">
        Drag fields between <strong>Available</strong> and <strong>Selected</strong> (or use the arrows). Drag within
        Selected to reorder. Labels apply to whichever column is configured. Additional columns come from the{' '}
        <strong>Product</strong> object’s fields (everything not hidden/trashed appears in Available alongside the built-in
        columns such as Action and list price).
      </p>
      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}
      {productPaletteWarning && (
        <Alert variant="warning" onClose={() => setProductPaletteWarning('')} dismissible>
          {productPaletteWarning}
        </Alert>
      )}

      <div className="d-flex align-items-start gap-2 text-muted small mb-4 p-2 bg-light border rounded">
        <FaInfoCircle className="mt-1 flex-shrink-0" />
        <span>
          Order in <strong>Selected</strong> is the column order in the modal. Rows without a Selected entry stay in
          Available (hidden).
        </span>
      </div>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="py-3 bg-secondary text-white border-0">
          <small className="text-white-50 d-block mb-1">Search step – table columns</small>
          <strong>Search results columns</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={5}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  <Badge bg="secondary" className="me-2">
                    {filteredSearchAvailable.length}
                  </Badge>
                  Available fields
                </h6>
              </div>
              <div className="mb-2 field-search-container">
                <FaSearch className="search-icon" />
                <Form.Control
                  type="text"
                  placeholder="Search by name..."
                  value={searchAvailTerm}
                  onChange={(e) => setSearchAvailTerm(e.target.value)}
                  className="field-search-input"
                  style={{
                    paddingLeft: '36px',
                    paddingRight: searchAvailTerm ? '36px' : '12px',
                  }}
                />
                {searchAvailTerm && (
                  <button
                    type="button"
                    className="clear-search-btn"
                    onClick={() => setSearchAvailTerm('')}
                    title="Clear"
                  >
                    <FaTimes size={12} />
                  </button>
                )}
              </div>
              <div className="border rounded p-2" style={{ maxHeight: '320px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                {filteredSearchAvailable.length === 0 ? (
                  <div className="text-center text-muted py-3 small">
                    No fields here
                  </div>
                ) : (
                  filteredSearchAvailable.map(key => (
                    <div
                      key={key}
                      className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => moveSearchToSelected(key)}
                    >
                      <div className="min-w-0">
                        <div className="fw-bold small">{resolveSearchColumnLabel(form, key)}</div>
                        <WizardColumnApiHint columnKey={key} />
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          moveSearchToSelected(key);
                        }}
                      >
                        <FaArrowRight />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </Col>
            <Col md={7}>
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h6 className="mb-0">
                  <Badge bg="primary" className="me-2">
                    {form.searchSelectedKeys.length}
                  </Badge>
                  Selected fields
                </h6>
                <small className="text-muted">Drag to reorder · Remove to send back</small>
              </div>
              <div className="border rounded p-2" style={{ maxHeight: '520px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                {form.searchSelectedKeys.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    Pick at least one column
                  </div>
                ) : (
                  form.searchSelectedKeys.map((key, index) => (
                    <div
                      key={key}
                      className="mb-2 p-2 bg-white rounded border"
                    >
                      <div
                        draggable
                        onDragStart={e => handleSearchDragStart(e, index)}
                        onDragOver={e => handleSearchDragOver(e, index)}
                        onDragLeave={handleSearchDragLeave}
                        onDrop={e => handleSearchDrop(e, index)}
                        onDragEnd={handleSearchDragEnd}
                        className={`d-flex justify-content-between align-items-center ${
                          draggedSearch === index ? 'opacity-50' : ''
                        } ${dragOverSearch === index ? 'border-bottom border-primary border-2 pb-2' : ''}`}
                        style={{ cursor: 'grab' }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 overflow-hidden">
                          <FaGripVertical className="text-muted flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="fw-bold small text-muted text-truncate">
                              {resolveSearchColumnLabel(form, key)}
                            </div>
                            <WizardColumnApiHint columnKey={key} />
                          </div>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="flex-shrink-0"
                          disabled={form.searchSelectedKeys.length <= 1}
                          onClick={() => moveSearchToAvailable(key)}
                          title={
                            form.searchSelectedKeys.length <= 1 ? 'Keep at least one column' : 'Remove'
                          }
                        >
                          <FaTrash size={14} />
                        </Button>
                      </div>
                      <Form.Label className="small text-muted mt-2 mb-1">Column header label</Form.Label>
                      <Form.Control
                        size="sm"
                        value={form.searchColumns[key]?.label ?? resolveSearchColumnLabel(form, key)}
                        onChange={e => setColumnLabel('search', key, e.target.value)}
                        placeholder={resolveSearchColumnLabel(form, key)}
                      />
                    </div>
                  ))
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm mb-4">
        <Card.Header className="py-3 bg-secondary text-white border-0">
          <small className="text-white-50 d-block mb-1">Edit step – line columns</small>
          <strong>Edit screen columns</strong>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={5}>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  <Badge bg="secondary" className="me-2">
                    {filteredEditAvailable.length}
                  </Badge>
                  Available fields
                </h6>
              </div>
              <div className="mb-2 field-search-container">
                <FaSearch className="search-icon" />
                <Form.Control
                  type="text"
                  placeholder="Search by name..."
                  value={editAvailTerm}
                  onChange={(e) => setEditAvailTerm(e.target.value)}
                  className="field-search-input"
                  style={{
                    paddingLeft: '36px',
                    paddingRight: editAvailTerm ? '36px' : '12px',
                  }}
                />
                {editAvailTerm && (
                  <button
                    type="button"
                    className="clear-search-btn"
                    onClick={() => setEditAvailTerm('')}
                    title="Clear"
                  >
                    <FaTimes size={12} />
                  </button>
                )}
              </div>
              <div className="border rounded p-2" style={{ maxHeight: '320px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                {filteredEditAvailable.length === 0 ? (
                  <div className="text-center text-muted py-3 small">
                    No fields here
                  </div>
                ) : (
                  filteredEditAvailable.map(key => (
                    <div
                      key={key}
                      className="d-flex justify-content-between align-items-center p-2 mb-2 bg-white rounded border cursor-pointer"
                      style={{ cursor: 'pointer' }}
                      onClick={() => moveEditToSelected(key)}
                    >
                      <div className="min-w-0">
                        <div className="fw-bold small">{resolveEditColumnLabel(form, key)}</div>
                        <WizardColumnApiHint columnKey={key} />
                      </div>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={e => {
                          e.stopPropagation();
                          moveEditToSelected(key);
                        }}
                      >
                        <FaArrowRight />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </Col>
            <Col md={7}>
              <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                <h6 className="mb-0">
                  <Badge bg="primary" className="me-2">
                    {form.editSelectedKeys.length}
                  </Badge>
                  Selected fields
                </h6>
                <small className="text-muted">Drag to reorder · Remove to send back</small>
              </div>
              <div className="border rounded p-2" style={{ maxHeight: '520px', overflowY: 'auto', backgroundColor: '#f8f9fa' }}>
                {form.editSelectedKeys.length === 0 ? (
                  <div className="text-center text-muted py-3">
                    Pick at least one column
                  </div>
                ) : (
                  form.editSelectedKeys.map((key, index) => (
                    <div
                      key={key}
                      className="mb-2 p-2 bg-white rounded border"
                    >
                      <div
                        draggable
                        onDragStart={e => handleEditDragStart(e, index)}
                        onDragOver={e => handleEditDragOver(e, index)}
                        onDragLeave={handleEditDragLeave}
                        onDrop={e => handleEditDrop(e, index)}
                        onDragEnd={handleEditDragEnd}
                        className={`d-flex justify-content-between align-items-center ${
                          draggedEdit === index ? 'opacity-50' : ''
                        } ${dragOverEdit === index ? 'border-bottom border-primary border-2 pb-2' : ''}`}
                        style={{ cursor: 'grab' }}
                      >
                        <div className="d-flex align-items-center gap-2 flex-grow-1 me-2 overflow-hidden">
                          <FaGripVertical className="text-muted flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="fw-bold small text-muted text-truncate">
                              {resolveEditColumnLabel(form, key)}
                            </div>
                            <WizardColumnApiHint columnKey={key} />
                          </div>
                        </div>
                        <Button
                          variant="outline-danger"
                          size="sm"
                          className="flex-shrink-0"
                          disabled={form.editSelectedKeys.length <= 1}
                          onClick={() => moveEditToAvailable(key)}
                          title={
                            form.editSelectedKeys.length <= 1 ? 'Keep at least one column' : 'Remove'
                          }
                        >
                          <FaTrash size={14} />
                        </Button>
                      </div>
                      <Form.Label className="small text-muted mt-2 mb-1">Column header label</Form.Label>
                      <Form.Control
                        size="sm"
                        value={form.editColumns[key]?.label ?? resolveEditColumnLabel(form, key)}
                        onChange={e => setColumnLabel('edit', key, e.target.value)}
                        placeholder={resolveEditColumnLabel(form, key)}
                      />
                    </div>
                  ))
                )}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <h6 className="mb-3 mt-4">Text overrides</h6>
      <Row className="g-3">
        {LINE_ITEM_WIZARD_STRING_KEYS.map((key) => (
          <Col md={6} key={key}>
            <Form.Label className="small mb-1 text-muted">{getLineItemWizardStringKeyLabel(key)}</Form.Label>
            <Form.Control
              size="sm"
              placeholder={key}
              value={form.strings[key] ?? ''}
              onChange={(e) =>
                setForm((f) => (!f ? f : { ...f, strings: { ...f.strings, [key]: e.target.value } }))
              }
            />
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default LineItemWizardConfigPanel;
